import { useCallback, type ReactNode } from 'react';
import type { Selection } from '../../hooks/useSelection.js';
import { MatrixCard } from './MatrixCard.js';
import { MatrixFooterRow } from './matrixCells.js';
import { MatrixRow as MatrixRowView } from './MatrixRow.js';
import type { MatrixRow, RowSpec } from './rowSpecs.js';
import { selectAllState } from './selectAllState.js';
import type { EnvironmentHandlers, MatrixColumn, OpenTarget, Paging } from './types.js';

/**
 * Records × environments, one kind of row — the Studios page's grid and the Games page's, told
 * apart only by `spec`. The name cell links to the record's editor; a status cell is the row's
 * OWN status (never a roll-up) and opens the drawer on it in that environment. Presentational:
 * rows in, clicks out. Below `sm` the page renders `MatrixCardList` instead.
 */
export const MatrixGrid = <Row extends MatrixRow>({
    spec,
    columns,
    rows,
    selection,
    onOpen,
    paging,
    toolbar,
    onEnvironment,
}: {
    spec: RowSpec<Row>;
    columns: MatrixColumn[];
    rows: Row[];
    selection: Selection;
    onOpen: (target: OpenTarget) => void;
    paging: Paging;
    toolbar: ReactNode;
    onEnvironment: EnvironmentHandlers;
}) => {
    const { checked, indeterminate } = selectAllState(rows, selection);
    // One stable callback for every row rather than a fresh one per row per render. Depends on
    // `selection.toggle` itself, NOT `selection` — `useMatrixPageState` rebuilds the `selection`
    // object on every render of the page that calls it (its `pulls`/`push`/`adoption` aren't
    // stabilised), so depending on the whole object would recreate `onToggle` — and therefore
    // every `MatrixRow`'s `onToggle` prop — on every such render, defeating `memo(MatrixRow)`.
    // `spec` is a module-level `STUDIO_ROWS`/`GAME_ROWS` constant, so it never breaks this either.
    const onToggle = useCallback((row: Row) => selection.toggle(spec.selectionOf(row)), [selection.toggle, spec]);

    return (
        <MatrixCard
            rows={rows}
            label={spec.label}
            columns={columns}
            toolbar={toolbar}
            extraHeader={spec.extraHeader}
            onEnvironment={onEnvironment}
            selectAll={{
                checked,
                indeterminate,
                onChange: (on) => selection.toggleAll(rows.map(spec.selectionOf), on),
                ariaLabel: spec.selectAllLabel,
            }}
        >
            {rows.map((row) => (
                <MatrixRowView
                    key={row.key}
                    spec={spec}
                    row={row}
                    columns={columns}
                    selected={selection.has(row)}
                    onToggle={onToggle}
                    onOpen={onOpen}
                />
            ))}
            <MatrixFooterRow colSpan={columns.length + 3} shown={rows.length} paging={paging} />
        </MatrixCard>
    );
};
